import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Calendar, Database, FileText, TrendingUp, UserPlus, Edit3, Settings, Globe, CheckCircle, Users as MeetingIcon, History, MessageSquare, Clock, Target, Building2 } from 'lucide-react';
import { publicationAPI, coauthorAPI } from '../services/api';
import api from '../services/api';
import { cacheService } from '../services/cache';
import { useAuth } from '../contexts/AuthContext';
import EditPublicationModal from '../components/Publications/EditPublicationModal';
import MeetingModal from '../components/Meetings/MeetingModal';
import MarkAsPublishedModal from '../components/Publications/MarkAsPublishedModal';
import EditHistoryModal from '../components/Publications/EditHistoryModal';
import AddCoauthorModal from '../components/Publications/AddCoauthorModal';
import ResubmissionFeedbackModal from '../components/Publications/ResubmissionFeedbackModal';
import TimelineModal from '../components/Publications/TimelineModal';
import ObjectiveModal from '../components/Publications/ObjectiveModal';
import TeamAffiliationModal from '../components/Publications/TeamAffiliationModal';
import logoImage from '../images/image.png';
import toast from 'react-hot-toast';

const PublicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [coauthorRequests, setCoauthorRequests] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [isMarkAsPublishedModalOpen, setIsMarkAsPublishedModalOpen] = useState(false);
  const [isEditHistoryModalOpen, setIsEditHistoryModalOpen] = useState(false);
  const [isResubmissionFeedbackModalOpen, setIsResubmissionFeedbackModalOpen] = useState(false);
  const [isAddCoauthorModalOpen, setIsAddCoauthorModalOpen] = useState(false);
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [isObjectiveModalOpen, setIsObjectiveModalOpen] = useState(false);
  const [isTeamAffiliationModalOpen, setIsTeamAffiliationModalOpen] = useState(false);

  const loadPublication = useCallback(async () => {
    try {
      setIsLoading(true);
      // Clear cache for this publication to ensure fresh data
      cacheService.clearPattern(`/publications/${id}/batch`);
      // Use batch endpoint to reduce API calls
      const response = await api.get(`/publications/${id}/batch`);
      const { publication, coauthorRequests, authors, stageHistory, dataSources } = response.data;
      // Attach authors, stageHistory, and dataSources to the publication data
      const publicationWithAuthors = {
        ...publication,
        authors,
        stageHistory,
        dataSources
      };
      setData(publicationWithAuthors);
      setCoauthorRequests(coauthorRequests);
    } catch (e: any) {
      toast.error('Failed to load publication');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadPublication();
  }, [id, loadPublication]);

  // Check if user can access Timeline and Objectives (superadmin, admin, or first author)
  const canAccessTimelineObjectives = () => {
    if (!user || !data) return false;
    return user.role === 'superadmin' || 
           user.role === 'admin' || 
           (data.lead_author_id && user.id === data.lead_author_id);
  };

  // Check if publication is in stage 2, 3, or 4
  const canShowTimelineObjectives = () => {
    if (!data) return false;
    return ['2', '3', '4'].includes(data.stage);
  };

  const view = useMemo(() => {
    if (!data) return null;
    // Map snake_case fields from API to camelCase used in UI where needed
    const submittedAt = data.submitted_at || data.submittedAt;
    // Get data source names from dataSources array, or fall back to data_source field
    const dataSourceNames = data.dataSources && data.dataSources.length > 0
      ? data.dataSources.map((ds: any) => ds.name).join(', ')
      : (data.data_source || data.dataSource || 'No data source specified.');
    const methods = data.methods;
    const stage = data.stage;
    const status = data.status;
    const title = data.title;
    const abstract = data.abstract;
    const leadAuthorName = data.first_name && data.last_name
      ? `${data.first_name} ${data.last_name}`
      : data.leadAuthorName;
    const authors: Array<any> = data.authors || [];
    const stageHistory: Array<any> = data.stageHistory || [];
    return { submittedAt, dataSource: dataSourceNames, methods, stage, status, title, abstract, leadAuthorName, authors, stageHistory };
  }, [data]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const handleRespondToRequest = async (requestId: number, status: 'approved' | 'rejected') => {
    try {
      await coauthorAPI.respondToRequest(requestId, {
        status,
        responseMessage: undefined // Message is optional
      });
      toast.success(`Co-author request ${status} successfully`);
      loadPublication(); // Reload to refresh data
    } catch (error: any) {
      toast.error(`Failed to ${status} co-author request`);
    }
  };

  const handlePublish = () => {
    if (!data?.id) return;
    setIsMarkAsPublishedModalOpen(true);
  };

  const handleChangeStage = async (newStage: '1' | '2' | '3' | '4' | 'published') => {
    if (!data || !id) return;
    
    try {
      await publicationAPI.updateStage(Number(id), { stage: newStage });
      
      // Clear cache to ensure fresh data
      cacheService.clearPattern(`/publications/${id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success(`Stage updated to ${getStageText(newStage)}`);
      loadPublication(); // Reload to get updated data
    } catch (error: any) {
      console.error('Error updating stage:', error);
      toast.error(error.response?.data?.error || 'Failed to update stage');
    }
  };

  const handleResubmit = async () => {
    if (!data?.id) return;
    
    const confirmResubmit = window.confirm(
      'Are you sure you want to resubmit this publication for review? This will change the status back to "Pending Review".'
    );
    
    if (confirmResubmit) {
      try {
        await publicationAPI.resubmitForReview(data.id);
        
        // Clear cache to ensure fresh data
        cacheService.clearPattern(`/publications/${data.id}/batch`);
        cacheService.clearPattern('/dashboard/batch');
        cacheService.clearPattern('/admin/batch');
        
        toast.success('Publication resubmitted successfully');
        loadPublication(); // Reload to refresh data
      } catch (error: any) {
        console.error('Resubmit error:', error);
        toast.error('Failed to resubmit publication');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageText = (stage: string) => {
    switch (stage) {
      case '1': return 'Ideation';
      case '2': return 'Review & Team';
      case '3': return 'Data Analysis';
      case '4': return 'Manuscript';
      case 'published': return 'Published';
      default: return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication System Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading publication...</p>
        </div>
      </div>
    );
  }

  if (!data || !view) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication System Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Publication Not Found</h2>
          <button onClick={() => navigate('/publications')} className="text-green-600 hover:text-green-700">Return to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <button onClick={() => navigate('/publications')} className="flex items-center space-x-1 text-green-600 hover:text-green-700">
            <ArrowLeft className="h-4 w-4" />
            <span>Dashboard</span>
          </button>
          <span>/</span>
          <span>{view.title}</span>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-start mb-3">
            <h1 className="text-2xl font-bold text-gray-900">{view.title}</h1>
            <div className="flex items-center space-x-3">
              {/* Edit History Button - visible to all users */}
              <button
                onClick={() => setIsEditHistoryModalOpen(true)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
                title="View edit history"
              >
                <History className="h-4 w-4" />
                <span className="text-sm">History</span>
              </button>
              
              {/* Edit Button - only for admins or lead author when needs resubmission */}
              {((user?.role === 'admin' || user?.role === 'superadmin') || 
                (user?.id === data?.lead_author_id && data?.status === 'Needs Resubmission')) && (
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors"
                >
                  <Edit3 className="h-4 w-4" />
                  <span className="text-sm">Edit</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
            {view.submittedAt && (
              <span className="inline-flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span>Submitted {formatDate(view.submittedAt)}</span>
              </span>
            )}
            {view.dataSource && (
              <span className="inline-flex items-center space-x-2">
                <Database className="h-4 w-4" />
                <span>{view.dataSource}</span>
              </span>
            )}
            <span className="inline-flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>{getStageText(view.stage)} • {view.status}</span>
            </span>
            {data?.status === 'Needs Resubmission' && (
              <span className="inline-flex items-center space-x-2 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                <Edit3 className="h-4 w-4" />
                <span>Returned for Revision</span>
              </span>
            )}
            {view.leadAuthorName && (
              <span className="inline-flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Lead: {view.leadAuthorName}</span>
              </span>
            )}
          </div>
          
          {/* Resubmission Guidance */}
          {data?.status === 'Needs Resubmission' && user?.id === data?.lead_author_id && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Edit3 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-2">Your publication needs revision</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    The Publication Committee has provided feedback on your submission. Please review their comments and make the necessary changes.
                  </p>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Next steps:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Click <strong>"View Feedback"</strong> below to see the committee's comments</li>
                      <li>Click the <strong>"Edit"</strong> button above to modify your publication</li>
                      <li>Review the feedback and make the requested changes</li>
                      <li>Click <strong>"Resubmit for Review"</strong> when you're ready to send it back</li>
                    </ol>
                    <div className="mt-3">
                      <button
                        onClick={() => setIsResubmissionFeedbackModalOpen(true)}
                        className="inline-flex items-center space-x-2 bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                      >
                        <MessageSquare className="h-4 w-4" />
                        <span>View Feedback</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" /> Abstract
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{view.abstract}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-green-600" /> Proposed Methods
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{view.methods || 'No methods specified.'}</p>
            </div>

            {/* Timeline and Objectives Section - Only show for stages 2, 3, 4 and for authorized users */}
            {canShowTimelineObjectives() && canAccessTimelineObjectives() && (
              <>
                {/* Timeline Block */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-blue-600" /> Project Timeline
                    </h2>
                    <button
                      onClick={() => setIsTimelineModalOpen(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <Clock className="h-4 w-4" />
                      <span>Manage Timeline</span>
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Track project milestones and deadlines to keep the publication on schedule.
                  </p>
                </div>

                {/* Objectives Block */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Target className="h-5 w-5 mr-2 text-purple-600" /> Project Objectives
                    </h2>
                    <button
                      onClick={() => setIsObjectiveModalOpen(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                    >
                      <Target className="h-4 w-4" />
                      <span>Manage Objectives</span>
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Define and track project objectives with file attachments to monitor progress.
                  </p>
                </div>
              </>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-green-600" /> Data Source
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{view.dataSource}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-green-600" /> Research Team
              </h2>
              {view.authors && view.authors.length ? (
                <div className="space-y-3">
                  {view.authors.map((a: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                          {(a.first_name && a.last_name
                            ? `${a.first_name[0]}${a.last_name[0]}`
                            : `${(a.firstName||'')[0]||''}${(a.lastName||'')[0]||''}`).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : `${a.firstName} ${a.lastName}`}
                          </div>
                          <div className="text-xs text-gray-600">{a.is_lead_author || a.isLeadAuthor ? 'Lead Author' : 'Co-author'}</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">Order {a.author_order || a.authorOrder}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No authors found.</p>
              )}

              {/* Co-author Requests Section */}
              {coauthorRequests.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-md font-semibold text-gray-900 mb-4">Co-author Requests ({coauthorRequests.length})</h3>
                  <div className="space-y-3">
                    {coauthorRequests.map((request) => (
                      <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                              {(() => {
                                const name = request.requester_name || request.requesterName || `${request.first_name || ''} ${request.last_name || ''}`.trim();
                                return name.split(' ').map((n: string) => n[0]).join('').toUpperCase();
                              })()}
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {request.requester_name || request.requesterName || `${request.first_name || ''} ${request.last_name || ''}`.trim()}
                              </h4>
                              <div className="text-sm text-gray-600">
                                {request.requester_email || request.requesterEmail || request.email}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-3">{request.message}</p>


                        {request.status === 'pending' && (Number(user?.id) === Number(data.lead_author_id || data.leadAuthorId) || user?.role === 'admin' || user?.role === 'superadmin') && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleRespondToRequest(request.id, 'approved')}
                              className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm"
                            >
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => handleRespondToRequest(request.id, 'rejected')}
                              className="flex items-center space-x-1 bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition-colors text-sm"
                            >
                              <span>Reject</span>
                            </button>
                          </div>
                        )}

                        {request.responseMessage && (
                          <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700">
                            <strong>Response:</strong> {request.responseMessage}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
              <div className="flex flex-wrap gap-3">
                {/* Timeline Button - Priority 1: For admin, superadmin, or lead author in stages 2-4 */}
                {canShowTimelineObjectives() && canAccessTimelineObjectives() && (
                  <button
                    onClick={() => setIsTimelineModalOpen(true)}
                    className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Clock className="h-4 w-4" />
                    <span>Timeline</span>
                  </button>
                )}

                {/* Join as Co-author Button - Priority 2 */}
                {(view.stage === '2' || view.stage === '3') && data.status !== 'Rejected' && user?.id !== (data.lead_author_id || data.leadAuthorId) && (
                  <Link
                    to={`/publications/${id}/coauthor`}
                    className="inline-flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Join as Co-author</span>
                  </Link>
                )}

                {/* Teams Button - Priority 3: For lead author, admin, or superadmin */}
                {user && 
                 (user.id === (data.lead_author_id || data.leadAuthorId) || 
                  user.role === 'admin' || 
                  user.role === 'superadmin') &&
                 data.status !== 'Rejected' && (
                  <button
                    onClick={() => setIsTeamAffiliationModalOpen(true)}
                    className="inline-flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Building2 className="h-4 w-4" />
                    <span>Teams</span>
                  </button>
                )}

                {/* Add Co-author - lead author only in stages 2-4 */}
                {user && 
                 user.id === (data.lead_author_id || data.leadAuthorId) && 
                 (view.stage === '2' || view.stage === '3' || view.stage === '4') &&
                 data.status !== 'Rejected' && (
                  <button
                    onClick={() => setIsAddCoauthorModalOpen(true)}
                    className="inline-flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Add Co-author</span>
                  </button>
                )}

                {/* Meeting Button - Only for lead authors in stages 2-4 */}
                {user && 
                 user.id === (data.lead_author_id || data.leadAuthorId) && 
                 (view.stage === '2' || view.stage === '3' || view.stage === '4') &&
                 data.status !== 'Rejected' && (
                  <button
                    onClick={() => setIsMeetingModalOpen(true)}
                    className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <MeetingIcon className="h-4 w-4" />
                    <span>Create Meeting</span>
                  </button>
                )}
                
                {/* Resubmit Button - For lead author when paper needs resubmission */}
                {user && 
                 user.id === (data.lead_author_id || data.leadAuthorId) && 
                 data.status === 'Needs Resubmission' && (
                  <button
                    onClick={handleResubmit}
                    className="inline-flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>Resubmit for Review</span>
                  </button>
                )}
              </div>
            </div>

            {/* Stage Management Section */}
            {(user?.role === 'admin' || user?.role === 'superadmin' || 
              (user?.id === (data.lead_author_id || data.leadAuthorId) && ['2', '3', '4'].includes(data.stage))) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Stage Management</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-3">
                      Current Stage: <span className="font-medium text-gray-900">{getStageText(data.stage)}</span>
                    </p>
                    {data.stage === '4' && (
                      <p className="text-xs text-gray-500 mb-3">Ready to publish? You can use the dropdown below or click the button to mark as published.</p>
                    )}
                    {(data.stage === '2' || data.stage === '3') && (
                      <p className="text-xs text-gray-500 mb-3">Select a new stage to progress your publication.</p>
                    )}
                  </div>
                  <div className="space-y-3">
                    {/* Stage Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Change Stage:</label>
                      <select
                        value={data.stage}
                        onChange={(e) => handleChangeStage(e.target.value as '1' | '2' | '3' | '4')}
                        className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 min-w-[200px]"
                      >
                        {/* Stage 1 only for admin/superadmin */}
                        {(user?.role === 'admin' || user?.role === 'superadmin') && (
                          <option value="1">1 · Ideation</option>
                        )}
                        <option value="2">2 · Review & Team</option>
                        <option value="3">3 · Data Analysis</option>
                        <option value="4">4 · Manuscript</option>
                      </select>
                    </div>
                    {/* Mark as Published Button (alternative for stage 4) */}
                    {data.stage === '4' && (
                      <div>
                        <button
                          onClick={handlePublish}
                          className="inline-flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Mark as Published</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Stage History</h2>
              {view.stageHistory && view.stageHistory.length ? (
                <div className="space-y-3">
                  {view.stageHistory.map((h: any, idx: number) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-900">{getStageText(h.to_stage || h.toStage)}</div>
                      <div className="text-xs text-gray-600">{h.created_at ? formatDate(h.created_at) : h.createdAt ? formatDate(h.createdAt) : ''}</div>
                      {h.notes && <div className="text-sm text-gray-700 mt-1">{h.notes}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No history available.</p>
              )}
            </div>
          </div>
        </div>

        <EditPublicationModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          publication={data}
          onUpdate={loadPublication}
        />

        <MeetingModal
          isOpen={isMeetingModalOpen}
          onClose={() => setIsMeetingModalOpen(false)}
          publicationId={data?.id}
          publicationTitle={data?.title}
          leadAuthorId={data?.lead_author_id}
          onMeetingCreated={() => {
            // Refresh any relevant data if needed
            console.log('Meeting created successfully');
          }}
        />

        <MarkAsPublishedModal
          isOpen={isMarkAsPublishedModalOpen}
          onClose={() => setIsMarkAsPublishedModalOpen(false)}
          publication={data}
          onSuccess={loadPublication}
        />

        <EditHistoryModal
          isOpen={isEditHistoryModalOpen}
          onClose={() => setIsEditHistoryModalOpen(false)}
          publicationId={data?.id || 0}
          publicationTitle={data?.title || ''}
        />

        <AddCoauthorModal
          isOpen={isAddCoauthorModalOpen}
          onClose={() => setIsAddCoauthorModalOpen(false)}
          publicationTitle={data?.title || ''}
          isLoading={false}
          onAddCoauthor={async ({ name, email, userId }) => {
            if (!data?.id) return;
            try {
              await coauthorAPI.addCoauthor(data.id, userId ? { userId } : { name, email });
              await loadPublication();
              toast.success('Co-author added');
            } catch (e: any) {
              toast.error(e?.response?.data?.error || 'Failed to add co-author');
            }
          }}
        />

        <ResubmissionFeedbackModal
          isOpen={isResubmissionFeedbackModalOpen}
          onClose={() => setIsResubmissionFeedbackModalOpen(false)}
          publicationId={data?.id || 0}
          publicationTitle={data?.title || ''}
        />

        <TimelineModal
          isOpen={isTimelineModalOpen}
          onClose={() => setIsTimelineModalOpen(false)}
          publicationId={data?.id || 0}
          publicationStage={data?.stage}
          onTimelineUpdated={() => {
            // Refresh any relevant data if needed
            console.log('Timeline updated successfully');
          }}
        />

        <ObjectiveModal
          isOpen={isObjectiveModalOpen}
          onClose={() => setIsObjectiveModalOpen(false)}
          publicationId={data?.id || 0}
          onObjectiveUpdated={() => {
            // Refresh any relevant data if needed
            console.log('Objectives updated successfully');
          }}
        />

        <TeamAffiliationModal
          isOpen={isTeamAffiliationModalOpen}
          onClose={() => setIsTeamAffiliationModalOpen(false)}
          publicationId={data?.id || 0}
          publicationTitle={data?.title || ''}
          onSuccess={() => {
            // Navigate to publications dashboard after successful team update
            navigate('/publications', { replace: true });
          }}
        />
      </div>
    </div>
  );
};

export default PublicationDetail;


